import { useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { ApiError } from '../api/client'
import { BottomSheet } from './BottomSheet'
import { PinPad } from './PinPad'

export interface PinSheetProps {
  open: boolean
  onClose: () => void
  /** What the PIN confirms, e.g. "Approve loan TS-0042". */
  title: string
  /** A short summary above the keys: amount, who, what happens next. */
  children?: ReactNode
  /**
   * Sends the request with the PIN. Resolve to close the sheet; throw an ApiError to
   * show its message ("That PIN is not right.") and let her try again.
   */
  onSubmit: (pin: string) => Promise<unknown>
}

/**
 * Step-up (PRD 12): approvals, payouts, offers and destination changes ask for the PIN
 * again. A wrong PIN clears the dots; five wrong PINs lock for 15 minutes.
 */
export function PinSheet({ open, onClose, title, children, onSubmit }: PinSheetProps) {
  const { t } = useTranslation()
  const [error, setError] = useState<string>()
  const [busy, setBusy] = useState(false)
  const [locked, setLocked] = useState(false)

  const close = () => {
    if (busy) return
    setError(undefined)
    setLocked(false)
    onClose()
  }

  const submit = async (pin: string) => {
    setBusy(true)
    setError(undefined)
    try {
      await onSubmit(pin)
      setBusy(false)
      onClose()
    } catch (e) {
      setBusy(false)
      if (e instanceof ApiError) {
        setError(e.message)
        if (e.code === 'pin_locked') setLocked(true)
      } else {
        setError(t('kit.errorState.body'))
      }
    }
  }

  return (
    <BottomSheet open={open} onClose={close} title={title}>
      {children && <div className="pin-sheet__summary">{children}</div>}
      <PinPad label={t('kit.pinSheet.label')} onComplete={(pin) => void submit(pin)} error={error}
        disabled={busy || locked} />
    </BottomSheet>
  )
}
